import { forEach } from 'lodash';
import { displayNextAvailableDay, getDateForApiDateStr } from '@chownow/cn-web-utils/date';
import { format } from 'date-fns';
import { readAddressFromLocalStorage } from 'helpers/localStorage';
import { ChowNowFacetTag as Tag } from 'types/restaurant';

export function extractGoogleMapComponents(addressComponents, formattedAddress) {
  // Split the address components into an object keyed by type
  // ie, route: { short_name: ..., long_name: ... }
  const googleComponents = addressComponents.reduce((acc, component) => {
    forEach(component.types, (type) => {
      acc[type] = component;
    });

    return acc;
  }, {});

  // Given a CN key, define how to grab it from the above `googleComponents` object
  const addressKeySelector = {
    street_address1: ({ street_number: streetNumber = {}, route = {} }) => {
      // Ensure closest match possible to formatted address;
      // default to long_name if no match on short name
      const selectedRoute = formattedAddress.includes(route.short_name)
        ? route.short_name
        : route.long_name;
      return `${streetNumber.short_name || ''} ${selectedRoute}`.trim();
    },
    street_address2: () => null,
    country_alpha2: ({ country = {} }) => country.short_name,
    country: ({ country = {} }) => {
      if (country.short_name === 'US') {
        return 'USA';
      }
      if (country.short_name === 'CA') {
        return 'CAN';
      }
      return false;
    },
    zip: ({ postal_code: zip = {} }) => zip.short_name,
    city: ({ locality, sublocality }) => {
      // preferred location of key; it can sometimes be under sublocality
      const googleKey = locality || sublocality || {};
      return googleKey.long_name;
    },
    state: ({ administrative_area_level_1: state = {} }) => state.short_name,
  };

  // Reduce over the keys we want to return to form an object comprised of the values we need
  return Object.keys(addressKeySelector).reduce((acc, key) => {
    acc[key] = addressKeySelector[key](googleComponents);
    return acc;
  }, {});
}

export function extractLatLng(gmapsAddress) {
  if (gmapsAddress.geometry) {
    return {
      lat:
        typeof gmapsAddress.geometry.location.lat === 'function'
          ? gmapsAddress.geometry.location.lat()
          : gmapsAddress.geometry.location.lat,
      lng:
        typeof gmapsAddress.geometry.location.lng === 'function'
          ? gmapsAddress.geometry.location.lng()
          : gmapsAddress.geometry.location.lng,
    };
  }
  return {
    lat: gmapsAddress.latitude,
    lng: gmapsAddress.longitude,
  };
}

export function deserializeAddress(address) {
  const gmapsAddress = address.gmaps || address;
  const addressComponents = gmapsAddress.address_components || [];
  if (!gmapsAddress.formatted_address) return false;

  return {
    formatted_address: gmapsAddress.place_formatted_address || gmapsAddress.formatted_address,
    types: gmapsAddress.types || [],
    ...extractLatLng(gmapsAddress),
    ...extractGoogleMapComponents(addressComponents, gmapsAddress.formatted_address),
  };
}

export function normalizeQueryParams(query) {
  return {
    ...query,
    query: query.query || '',
    isOpen: query.isOpen === '1',
    deliversToMe: query.deliversToMe === '1',
    hasCurbside: query.hasCurbside === '1',
    hasPromo: query.hasPromo === '1',
  };
}

export function getAvailableOptionsLabel({
  isCurbsideAvailableNow,
  isDeliveryAvailableNow,
  isPickupAvailableNow,
}) {
  const fulfillmentTypes = [];
  if (isPickupAvailableNow) fulfillmentTypes.push('Pickup');
  if (isCurbsideAvailableNow) fulfillmentTypes.push('Curbside');
  if (isDeliveryAvailableNow) fulfillmentTypes.push('Delivery');
  return fulfillmentTypes.join(' · ');
}

export function getOpeningTimeLabel({
  isFilteredForDelivery,
  nextDeliveryTime,
  nextPickupTime,
  orderAhead,
}) {
  const date = isFilteredForDelivery
    ? getDateForApiDateStr(nextDeliveryTime)
    : getDateForApiDateStr(nextPickupTime);
  const orderAheadLabel = Object.values(orderAhead).every((v) => v === false)
    ? ''
    : 'Order ahead · ';
  return `${orderAheadLabel}Opens ${displayNextAvailableDay(date)} at ${format(date, 'h:mma')}`;
}

export function getShouldShowAllOptions({
  isFilteredForDelivery,
  isDeliveryAvailableNow,
  isPickupAvailableNow,
}) {
  return (
    (isFilteredForDelivery && isDeliveryAvailableNow) ||
    (!isFilteredForDelivery && isPickupAvailableNow)
  );
}

function getShowOrderAheadTime({
  isFilteredForDelivery,
  isDeliveryAvailableNow,
  isPickupAvailableNow,
  nextDeliveryTime,
  nextPickupTime,
}) {
  return (
    (isFilteredForDelivery && !isDeliveryAvailableNow && nextDeliveryTime) ||
    (!isFilteredForDelivery && !isPickupAvailableNow && nextPickupTime)
  );
}
export function formatFulfillmentTypes({
  isCurbsideAvailableNow,
  isFilteredForDelivery,
  isDeliveryAvailableNow,
  isPickupAvailableNow,
  nextPickupTime,
  nextDeliveryTime,
  orderAhead,
}) {
  const showAllOptions = getShouldShowAllOptions({
    isFilteredForDelivery,
    isDeliveryAvailableNow,
    isPickupAvailableNow,
  });

  const showOrderAheadTime = getShowOrderAheadTime({
    isFilteredForDelivery,
    isDeliveryAvailableNow,
    nextDeliveryTime,
    isPickupAvailableNow,
    nextPickupTime,
  });

  if (showAllOptions) {
    return getAvailableOptionsLabel({
      isCurbsideAvailableNow,
      isDeliveryAvailableNow,
      isPickupAvailableNow,
    });
  }

  if (showOrderAheadTime) {
    return getOpeningTimeLabel({
      isFilteredForDelivery,
      nextDeliveryTime,
      nextPickupTime,
      orderAhead,
    });
  }

  return 'Ordering unavailable';
}

const DELIVERY_FULFILLMENT_QUERY = '?deliversToMe=1';
const DELIVERY_LOCATION_QUERY = '&loc=';
const DELIVERY_SAVED_ADDRESS_ID_QUERY = '&savedAddressId=';

export function getDeliveryAddressQueryFromFilters(searchFilters) {
  if (!searchFilters.loc) return '';

  return `${DELIVERY_LOCATION_QUERY}${encodeURIComponent(searchFilters.loc)}`;
}

function appendSavedAddressId() {
  const savedAddressId = readAddressFromLocalStorage().saved_address_id;
  if (!savedAddressId) return '';

  return `${DELIVERY_SAVED_ADDRESS_ID_QUERY}${encodeURIComponent(savedAddressId)}`;
}

// Add fulfillment and address information to URL for delivery orders
export function formatOutgoingQueryParams(searchFilters) {
  if (!searchFilters.deliversToMe) return '';

  return `${DELIVERY_FULFILLMENT_QUERY}${getDeliveryAddressQueryFromFilters(
    searchFilters
  )}${appendSavedAddressId()}`;
}

export function getParamsWithAction(action, searchFiltersFromURL) {
  const searchFilters =
    action === 'default_to_delivery'
      ? { ...searchFiltersFromURL, deliversToMe: true }
      : searchFiltersFromURL;
  return formatOutgoingQueryParams(searchFilters);
}

export const ALLOWED_FACETS = [Tag.IsFavorite, Tag.IsNew, Tag.LowCostDelivery];
